import React, { useState, useEffect } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { ItineraryItem } from '../../types';
import { fetchApi } from '../../services/apiBase';
import './ActivityActionSheet.css';

interface SwapSuggestion {
  title: string;
  place: string;
  desc: string;
}

interface DayOption {
  date: string;
  label: string;
  dayNum: number;
}

interface ActivityActionSheetProps {
  item: ItineraryItem;
  dayNum: number;
  itineraryId: number;
  days: DayOption[];
  onClose: () => void;
  onRemove: (itemId: number) => void;
  onSwap: (itemId: number, suggestion: SwapSuggestion) => void;
  onMove: (itemId: number, newDate: string) => void;
  onSaveNote: (itemId: number, note: string) => void;
  /** Toggle the chat lock (slice 2). Confirmed activities are refused by
   * the AI edit contract server-side; this is where a user sets one. */
  // Resolves to a failure message, or null when the lock was saved. The
  // message is rendered HERE rather than in the page's error banner, which
  // this sheet paints over while it stays open.
  onToggleLock: (itemId: number, confirmed: boolean) => Promise<string | null> | void;
}

const ACTIONS = [
  { icon: '↻', label: 'Swap' },
  { icon: '✕', label: 'Remove' },
  { icon: '↕', label: 'Move' },
  { icon: '✎', label: 'Note' },
  { icon: '⚑', label: 'Report' },
] as const;

/** The lock is deliberately NOT in ACTIONS: those open a panel, this
 *  applies immediately and carries state, and its label says what the
 *  next tap will do — and so does its icon: Lock to confirm, Unlock to
 *  unconfirm. */
const LOCK_ACTION = { lock: 'Confirm', unlock: 'Unconfirm' } as const;

const REPORT_OPTIONS = [
  'Permanently closed',
  'Temporarily closed',
  'Changed hours',
  'Changed location',
  'Wrong information',
  'Still open and correct',
];

export const ActivityActionSheet: React.FC<ActivityActionSheetProps> = ({
  item,
  dayNum,
  itineraryId,
  days,
  onClose,
  onRemove,
  onSwap,
  onMove,
  onSaveNote,
  onToggleLock,
}) => {
  const [activePanel, setActivePanel] = useState<'swap' | 'move' | 'note' | 'report' | null>(null);
  const [lockPending, setLockPending] = useState(false);
  const [lockError, setLockError] = useState<string | null>(null);
  const [swapSuggestions, setSwapSuggestions] = useState<SwapSuggestion[]>([]);
  const [loadingSwap, setLoadingSwap] = useState(false);
  const [reported, setReported] = useState(false);
  const [noteText, setNoteText] = useState(item.notes || '');

  // Reset swap suggestions when panel changes (prevents stale data)
  useEffect(() => {
    if (activePanel !== 'swap') {
      setSwapSuggestions([]);
    }
  }, [activePanel]);

  // Fetch swap suggestions from AI
  useEffect(() => {
    if (activePanel !== 'swap' || swapSuggestions.length > 0) return;
    let cancelled = false;
    setLoadingSwap(true);
    const params = new URLSearchParams({
      activity: item.name,
      location: item.location || '',
      itinerary_id: String(itineraryId),
      type: item.type || 'activity',
      time: item.time || '',
    });
    fetchApi<{ suggestions: SwapSuggestion[] }>(
      `/api/v1/plans/swap-suggestions/?${params}`
    )
      .then(data => {
        if (!cancelled) setSwapSuggestions(data.suggestions || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoadingSwap(false); });
    return () => { cancelled = true; };
  }, [activePanel, item.name, item.location, item.type, item.time, itineraryId, swapSuggestions.length]);

  const handleAction = (label: string) => {
    // A lock failure is about the lock, so it must not outlive the user moving
    // on to something else. Merely HIDING it while a panel is open (which the
    // render used to do) meant closing the panel re-showed it — and re-announced
    // it, `role="alert"` — long after the message stopped being true.
    setLockError(null);
    switch (label) {
      case 'Swap':
        setActivePanel('swap');
        break;
      case 'Remove':
        onRemove(item.id);
        onClose();
        break;
      case 'Move':
        setActivePanel('move');
        break;
      case 'Note':
        setActivePanel('note');
        break;
      case 'Report':
        setActivePanel('report');
        break;
    }
  };

  return (
    <div className="act-sheet-backdrop" onClick={onClose}>
      <div className="act-sheet" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="act-sheet-header">
          <div className="act-sheet-header-body">
            <div className="act-sheet-title">{item.name}</div>
            {item.location && <div className="act-sheet-place">{item.location}</div>}
            {item.notes && !activePanel && <div className="act-sheet-desc">{item.notes}</div>}
          </div>
          <div className="act-sheet-day">Day {dayNum}</div>
        </div>

        {/* Quick actions */}
        <div className="act-sheet-actions">
          {ACTIONS.map(a => (
            <button
              key={a.label}
              className={`act-sheet-action ${a.label === 'Report' ? 'act-sheet-action--warn' : ''} ${activePanel === a.label.toLowerCase() ? 'act-sheet-action--active' : ''}`}
              onClick={() => handleAction(a.label)}
            >
              <span className="act-sheet-action-icon">{a.icon}</span>
              <span className="act-sheet-action-label">{a.label}</span>
            </button>
          ))}
          {/* aria-disabled, not `disabled`: disabling the focused button
              mid-flight drops keyboard focus to the body, and the closure
              guard in onClick already prevents the double-send. */}
          <button
            className={`act-sheet-action ${item.confirmed ? 'act-sheet-action--locked' : ''}`}
            aria-disabled={lockPending}
            onClick={async () => {
              // Re-entrancy guard: a second tap before the first round-trip
              // returns would send the same (now stale) revision and 409.
              if (lockPending) return;
              setLockPending(true);
              setLockError(null);
              try {
                setLockError((await onToggleLock(item.id, !item.confirmed)) ?? null);
              } finally {
                setLockPending(false);
              }
            }}
          >
            <span className="act-sheet-action-icon">
              {item.confirmed
                ? <Unlock size={16} aria-hidden="true" />
                : <Lock size={16} aria-hidden="true" />}
            </span>
            <span className="act-sheet-action-label">
              {item.confirmed ? LOCK_ACTION.unlock : LOCK_ACTION.lock}
            </span>
          </button>
        </div>

        {item.confirmed && !activePanel && (
          <div className="act-sheet-locked-note">
            Confirmed — AI chat won&apos;t change this activity.
          </div>
        )}

        {lockError && (
          <div className="act-sheet-lock-error" role="alert">
            {lockError}
          </div>
        )}

        {/* Swap suggestions */}
        {activePanel === 'swap' && (
          <div className="act-sheet-section">
            <div className="act-sheet-section-label">Swap with</div>
            {loadingSwap ? (
              <div className="act-sheet-loading">Finding alternatives...</div>
            ) : swapSuggestions.length === 0 ? (
              <div className="act-sheet-loading">No suggestions available</div>
            ) : (
              swapSuggestions.map((s, i) => (
                <button
                  key={i}
                  className="act-sheet-swap-card"
                  onClick={() => onSwap(item.id, s)}
                >
                  <div className="act-sheet-swap-num">{i + 1}</div>
                  <div className="act-sheet-swap-body">
                    <div className="act-sheet-swap-title">{s.title}</div>
                    <div className="act-sheet-swap-place">{s.place}</div>
                    <div className="act-sheet-swap-desc">{s.desc}</div>
                  </div>
                </button>
              ))
            )}
          </div>
        )}

        {/* Move to day */}
        {activePanel === 'move' && (
          <div className="act-sheet-section">
            <div className="act-sheet-section-label">Move to</div>
            {days.filter(d => d.date !== item.date).map(d => (
              <button
                key={d.date}
                className="act-sheet-move-day"
                onClick={() => onMove(item.id, d.date)}
              >
                <span className="act-sheet-move-day-num">Day {d.dayNum}</span>
                <span className="act-sheet-move-day-label">{d.label}</span>
              </button>
            ))}
            {days.filter(d => d.date !== item.date).length === 0 && (
              <div className="act-sheet-loading">Only one day in this plan</div>
            )}
          </div>
        )}

        {/* Note editor */}
        {activePanel === 'note' && (
          <div className="act-sheet-section">
            <div className="act-sheet-section-label">Notes</div>
            <textarea
              className="act-sheet-note-input"
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="Add a note..."
              rows={3}
              autoFocus
            />
            <div className="act-sheet-note-actions">
              <button
                className="act-sheet-note-save"
                onClick={() => onSaveNote(item.id, noteText)}
              >
                Save
              </button>
              <button
                className="act-sheet-note-cancel"
                onClick={() => setActivePanel(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Report section */}
        {activePanel === 'report' && !reported && (
          <div className="act-sheet-section">
            <div className="act-sheet-section-label">Report an issue</div>
            {REPORT_OPTIONS.map((r) => (
              <button
                key={r}
                className={`act-sheet-report-btn ${r.includes('correct') ? 'act-sheet-report-btn--ok' : ''}`}
                onClick={() => setReported(true)}
              >
                {r.includes('correct') ? '✓ ' : ''}{r}
              </button>
            ))}
          </div>
        )}
        {reported && (
          <div className="act-sheet-section">
            <div className="act-sheet-loading">Thanks for reporting!</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityActionSheet;
